import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Info } from 'lucide-react';

const Materials = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMaterials = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/materials');
        const data = await response.json();
        setMaterials(data);
      } catch (error) {
        console.error('Error fetching materials:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMaterials();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 pt-40 md:pt-64 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-black text-zinc-900 mb-4 uppercase tracking-tight">
            Vores <span className="text-black">Materialer</span>
          </h1>
          <p className="text-xl text-zinc-600 max-w-2xl">
            Vi tilbyder et bredt udvalg af kvalitetsmaterialer til dit næste projekt. 
            Alle priser er inkl. moms og gælder ved afhentning.
          </p>
        </div>

        {/* Materials Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-8">
          {materials.map((material) => (
            <Link 
              key={material.id} 
              to={`/materialer/${material.slug.da}`}
              className="group bg-white rounded-none shadow-sm border border-zinc-100 overflow-hidden hover:shadow-xl transition-all duration-300 flex flex-col"
            >
              {/* Image Container */}
              <div className="relative aspect-[4/3] overflow-hidden">
                <img 
                  src={material.image?.[0] || 'https://via.placeholder.com/400x300'} 
                  alt={material.name?.da}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-white/90 backdrop-blur-sm text-zinc-900 px-3 py-1 rounded-none text-sm font-bold uppercase tracking-wider shadow-sm">
                    {material.category}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 flex flex-col flex-grow">
                <div className="flex justify-between items-start mb-4">
                  <h2 className="text-2xl font-bold text-black group-hover:text-black transition-colors">
                    {material.name?.da}
                  </h2>
                  <div className="text-right">
                    {material.price_per_unit ? (
                      <>
                        <p className="text-xs text-zinc-500 uppercase font-bold tracking-widest leading-none mb-1">Pris</p>
                        <p className="text-2xl font-black text-black leading-none">
                          {material.price_per_unit},-
                        </p>
                        <p className="text-xs text-zinc-400 font-medium">kr. / stk</p>
                      </>
                    ) : (
                      <>
                        <p className="text-xs text-zinc-500 uppercase font-bold tracking-widest leading-none mb-1">Pris fra</p>
                        <p className="text-2xl font-black text-black leading-none">
                          {material.price_murerbalje},-
                        </p>
                        <p className="text-xs text-zinc-400 font-medium">kr. / balje</p>
                      </>
                    )}
                  </div>
                </div>

                <div 
                  className="text-zinc-600 mb-8 line-clamp-3 prose prose-sm"
                  dangerouslySetInnerHTML={{ __html: material.description?.da }}
                />

                <div className="mt-auto pt-6 border-t border-zinc-50 flex items-center justify-between">
                  <div className="flex items-center text-zinc-400 text-sm font-medium">
                    <Info size={16} className="mr-2" />
                    Inkl. moms
                  </div>
                  <div className="inline-flex items-center font-bold text-zinc-900 group-hover:text-black transition-colors">
                    Læs mere
                    <ChevronRight size={20} className="ml-1 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Call to Action */}
        <div className="mt-20 bg-[#eedec0] rounded-none p-8 md:p-12 text-zinc-900 relative overflow-hidden">
          <div className="relative z-10 max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-black mb-4 uppercase text-zinc-900">Har du brug for hjælp?</h2>
            <p className="text-lg text-zinc-800 mb-8 font-medium">
              Er du i tvivl om hvilket materiale der passer bedst til din opgave, eller har du brug for en større mængde? Ring til os for rådgivning.
            </p>
            <a 
              href="tel:+4520181271"
              className="inline-flex items-center bg-black text-white px-8 py-4 rounded-none font-black text-lg hover:bg-zinc-900 transition-all shadow-xl"
            >
              Ring til os nu
            </a>
          </div>
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-black/5 rounded-full -mr-32 -mt-32 blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-black/5 rounded-full -mr-48 -mb-48 blur-3xl"></div>
        </div>
      </div>
    </div>
  );
};

export default Materials;
